import { InfoOutlined, SendRounded } from '@mui/icons-material';
import { IconButton, TextareaAutosize } from '@mui/material';
import { useState } from 'react';
import defaultChatAvatar from '../../../../assets/chat_avatar.svg';
import yourCompanyLogo from '../../../../assets/your_company.png';
import { ChatSize } from '../../../../enums/ChatAgentCreationStep';
import { useTranslation } from '../../../../hooks/useTranslation';
import ImageAvatar from '../ImageAvatar/ImageAvatar';
import StyledChatWindow from '../StyleChatWindow';
import PrivacyPolicyContent from './PrivacyPolicyContent';
import StyledActionButton from './StyledActionButton';

interface RenderChatWindowProps {
  chatAvatar?: string;
  chatIntro?: string;
  chatSize?: ChatSize;
  chatTitle?: string;
  disclaimerHtml?: string;
  headerAvatar?: string;
  message: string;
  promptSuggestions?: string[];
  themeColor?: string;
  onMessageChange: (value: string) => void;
}

const ChatWindow: React.FC<RenderChatWindowProps> = ({
  chatAvatar,
  chatIntro,
  chatSize,
  chatTitle,
  disclaimerHtml,
  headerAvatar,
  message,
  promptSuggestions,
  themeColor,
  onMessageChange,
}) => {
  const { t } = useTranslation();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  // Use provided values or fallback to defaults
  const headerAvatarSource = headerAvatar || yourCompanyLogo;
  const chatAvatarSource = chatAvatar || defaultChatAvatar;
  const headerBackground = themeColor
    ? `linear-gradient(45deg, ${themeColor}, ${themeColor}80)`
    : 'linear-gradient(45deg, rgb(23, 65, 255), rgb(150, 169, 255))';

  return (
    <StyledChatWindow chatSize={chatSize} style={{ position: 'relative' }}>
      {/* Header */}
      <div
        style={{
          boxSizing: 'border-box',
          background: headerBackground,
          height: '90px',
          borderRadius: '20px 20px 0 0',
          display: 'flex',
          padding: '18px',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <ImageAvatar
            imageSource={headerAvatarSource}
            altText={t('Company logo')}
          />
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#fff',
              fontFamily: 'Helvetica, Arial, sans-serif',
            }}
          >
            {chatTitle || 'Elora'}
          </h1>
        </div>
        <IconButton
          aria-label={t('Privacy policy')}
          onClick={() => setShowPrivacyPolicy(prev => !prev)}
          sx={{ padding: 0, alignSelf: 'flex-start' }}
        >
          <InfoOutlined sx={{ color: '#fff' }} />
        </IconButton>
      </div>
      {/* Content area wrapper (relative for disclaimer/privacy overlays) */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Conversation Section */}
        <div
          style={{
            boxSizing: 'border-box',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '18px',
            overflowY: 'scroll',
            gap: '18px',
          }}
        >
          <div
            style={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              gap: '18px',
              alignItems: 'flex-end',
            }}
          >
            <ImageAvatar
              imageSource={chatAvatarSource}
              altText={t('Chat avatar')}
              width="34px"
              height="34px"
            />
            <p
              style={{
                margin: '0',
                flex: 1,
                backgroundColor: 'rgb(245, 245, 245)',
                borderRadius: '8px',
                padding: '18px',
                fontSize: '16px',
              }}
            >
              {chatIntro ||
                t(
                  "Hello! I'm Elo, your friendly assistant here on the Elora website. How can I assist you today?"
                )}
            </p>
          </div>
          {promptSuggestions && promptSuggestions.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              {promptSuggestions.map((suggestion, index) => (
                <StyledActionButton
                  key={index}
                  themeColor={themeColor}
                  chatSize={'16px'}
                >
                  {suggestion}
                </StyledActionButton>
              ))}
            </div>
          )}
        </div>
        {/* Chat Input */}
        <div
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            minHeight: '76px',
            borderTop: '1px solid lightgray',
            padding: '18px',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '18px',
          }}
        >
          <TextareaAutosize
            name="chat_message"
            placeholder={t('Type your message')}
            value={message}
            onChange={event => onMessageChange(event.target.value)}
            style={{
              fontSize: '16px',
              lineHeight: '16px',
              fontFamily: 'var(--default-fonts)',
              border: 'none',
              outline: 'none',
              width: '100%',
              overflow: 'hidden',
              resize: 'none',
              backgroundColor: 'white',
            }}
            minRows="1"
            maxRows="8"
            maxLength={200}
            disabled
          ></TextareaAutosize>
          <SendRounded
            style={{
              width: '28px',
              height: '28px',
              fill: message.trim() ? 'rgb(23, 65, 255)' : 'gray',
            }}
          />
        </div>
        {/* Privacy Policy Overlay */}
        {showPrivacyPolicy && (
          <div
            style={{
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: '16px',
              position: 'absolute',
              top: '48px',
              left: '4%',
              width: '90%',
              maxHeight: '65%',
              backgroundColor: '#fff',
              overflowY: 'auto',
              padding: '24px',
              zIndex: 2,
              borderRadius: '16px',
              boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 20px',
            }}
          >
            <PrivacyPolicyContent />
          </div>
        )}
        {/* Disclaimer Modal Overlay */}
        {showDisclaimer && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: '#fff',
              overflowY: 'auto',
              padding: '18px',
              zIndex: 1,
              width: '80%',
              height: '80%',
              borderRadius: '20px',
              boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 20px',
              margin: '5% auto',
            }}
          >
            <div
              style={{ fontSize: '14px', lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{
                __html: disclaimerHtml || '',
              }}
            />
          </div>
        )}
      </div>
      {/* Footer */}
      <div
        style={{
          backgroundColor: 'rgb(245, 245, 245)',
          height: 'fit-content',
          textAlign: 'center',
          fontSize: '8px',
          fontWeight: 700,
          borderRadius: '0 0 20px 20px',
        }}
      >
        <p>
          {t('Made by Elora')} &middot;{' '}
          <span
            style={{
              cursor: disclaimerHtml ? 'pointer' : 'default',
              textDecoration: disclaimerHtml ? 'underline' : 'none',
            }}
            onClick={() => {
              if (disclaimerHtml) {
                setShowDisclaimer(prev => !prev);
              }
            }}
          >
            {t('Disclaimer')}
          </span>
        </p>
      </div>
    </StyledChatWindow>
  );
};

export default ChatWindow;
